import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import { useAuth } from '../context/AuthContext';
import { getUserProfile } from '../services/profilesService';
import { getVendor } from '../services/vendorsService';
import { listEvents } from '../services/eventsService';
import { getPrecheckin, upsertPrecheckin } from '../services/precheckinsService';
import './ProfilePage.css';

const CHECKINS_COLLECTION = 'checkins';

const sortBySequence = (rows) => {
  const list = Array.isArray(rows) ? [...rows] : [];
  list.sort((a, b) => {
    const av = Number(a?.sequence ?? 0);
    const bv = Number(b?.sequence ?? 0);
    if (Number.isFinite(av) && Number.isFinite(bv) && av !== bv) return av - bv;
    return String(a?.name || a?.date || '').localeCompare(String(b?.name || b?.date || ''));
  });
  return list;
};

const normalizeRequiredKey = (item, index) => {
  const seq = Number(item?.sequence ?? (index + 1));
  const name = String(item?.name || '').trim();
  return `${seq}::${name}`;
};

const normalizeBoothNumber = (value) => String(value || '').trim();

const getReservationBoothNumber = (row) => {
  const candidate =
    row?.boothNumber ??
    row?.number ??
    row?.boothNo ??
    row?.booth ??
    row?.boothId ??
    '';
  return normalizeBoothNumber(candidate);
};

const normalizeRequiredBoothItemKey = (boothKey, item, index) => {
  return `${String(boothKey || '').trim()}::${normalizeRequiredKey(item, index)}`;
};

const isVendorCheckedIn = (checkinData) => {
  if (!checkinData) return false;
  return checkinData.checkedIn === true || (checkinData.checkedIn == null && Boolean(checkinData.checkinAt));
};

const getCurrentConsentDateDisplay = () => {
  try {
    return new Date().toLocaleString();
  } catch {
    return String(new Date());
  }
};

const normalizeSectionFlag = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'required') return 'required';
  if (normalized === 'optional') return 'optional';
  if (normalized === 'do not display' || normalized === 'off' || normalized === 'hidden' || normalized === 'none') {
    return 'do not display';
  }
  return 'required';
};

const sectionTitleStyle = {
  marginBottom: 10,
  fontSize: 18,
  fontWeight: 800,
  color: '#16324f',
  padding: '6px 10px',
  borderLeft: '4px solid #1976d2',
  background: '#eef6ff',
  borderRadius: 4,
};

const PreCheckinSteps = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const initialEventId = String(searchParams.get('eventId') || '').trim();
  const initialVendorId = String(searchParams.get('vendorId') || '').trim();
  const returnTo = String(searchParams.get('returnTo') || '').trim();
  const source = String(searchParams.get('source') || '').trim().toLowerCase();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [userProfile, setUserProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(initialEventId);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(initialVendorId);
  const [vendorName, setVendorName] = useState('');
  const [vendorBooths, setVendorBooths] = useState([]);

  const [eventData, setEventData] = useState(null);
  const [precheckin, setPrecheckin] = useState(null);
  const [checkinRecord, setCheckinRecord] = useState(null);
  const [hasPurchasedBooth, setHasPurchasedBooth] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [selectedScheduleSequence, setSelectedScheduleSequence] = useState('');
  const [requiredSelections, setRequiredSelections] = useState({});
  const [consentName, setConsentName] = useState('');
  const [consentDate, setConsentDate] = useState(getCurrentConsentDateDisplay());
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [paperConsentForm, setPaperConsentForm] = useState(false);

  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentViewed, setConsentViewed] = useState(false);

  const userRole = String(userProfile?.userType || '').trim();
  const isAdminLike = ['Admin', 'SystemAdmin', 'Organizer', 'EventAdmin'].includes(userRole);
  const isVendor = userRole === 'Vendor';

  const handleExitPage = (fallbackPath) => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    const eventForReturn = String(selectedEvent || initialEventId || '').trim();
    const vendorForReturn = String((isVendor ? userProfile?.vendorId : selectedVendor) || initialVendorId || '').trim();
    if (source === 'vendor-checkin' && eventForReturn && vendorForReturn) {
      const params = new URLSearchParams();
      params.set('eventId', eventForReturn);
      navigate(`/admin/bazaar-checkin/${vendorForReturn}?${params.toString()}`);
      return;
    }
    navigate(fallbackPath);
  };

  const sortedCheckinSchedule = useMemo(
    () => sortBySequence(eventData?.checkinSchedule || []),
    [eventData?.checkinSchedule]
  );
  const sortedRequiredItems = useMemo(
    () => sortBySequence(eventData?.requiredItems || []),
    [eventData?.requiredItems]
  );

  const checkinScheduleFlag = normalizeSectionFlag(
    eventData?.checkinScheduleFlag ?? eventData?.CheckInScheduleFlag
  );
  const requiredItemsFlag = normalizeSectionFlag(
    eventData?.requiredItemsFlag ?? eventData?.RequiredItemsFlag
  );

  const showCheckinScheduleSection = checkinScheduleFlag !== 'do not display';
  const showRequiredItemsSection = requiredItemsFlag !== 'do not display';
  const isCheckinScheduleRequired = checkinScheduleFlag === 'required';
  const isRequiredItemsRequired = requiredItemsFlag === 'required';

  const signed = precheckin?.consentSigned === true || precheckin?.consent?.signed === true;
  const checkedIn = isVendorCheckedIn(checkinRecord);
  const readOnly = checkedIn || !hasPurchasedBooth || (signed && !isEditing);
  const showSignedStatus = signed && !isEditing;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const profile = await getUserProfile(currentUser.uid);
        if (cancelled) return;
        setUserProfile(profile || null);

        const allEvents = await listEvents();
        if (cancelled) return;

        const uidLower = String(currentUser.uid || '').trim().toLowerCase();
        const role = String(profile?.userType || '').trim();

        let visibleEvents = Array.isArray(allEvents) ? [...allEvents] : [];

        if (role === 'EventAdmin') {
          visibleEvents = visibleEvents.filter(ev =>
            Array.isArray(ev?.eventAdmins) &&
            ev.eventAdmins.some(a => String(a || '').trim().toLowerCase() === uidLower)
          );
        } else if (role === 'Organizer') {
          visibleEvents = visibleEvents.filter(ev => {
            const organizerList =
              (Array.isArray(ev?.organizers) && ev.organizers) ||
              (Array.isArray(ev?.eventOrganizers) && ev.eventOrganizers) ||
              (Array.isArray(ev?.EventOrganizers) && ev.EventOrganizers) ||
              [];
            return organizerList.some(a => String(a || '').trim().toLowerCase() === uidLower);
          });
        } else if (role === 'Vendor') {
          const vendorId = String(profile?.vendorId || '').trim();
          if (!vendorId) {
            visibleEvents = [];
          } else {
            const reservationsCol = collection(db, 'boothReservations');
            const vendorReservationsQuery = query(
              reservationsCol,
              where('vendorId', '==', vendorId),
              where('status', '==', 'Reserved')
            );
            const vendorReservationsSnap = await getDocs(vendorReservationsQuery);
            const reservedEventIds = new Set();
            vendorReservationsSnap.forEach(d => {
              const eventId = d.data()?.eventId;
              if (eventId) reservedEventIds.add(String(eventId));
            });
            visibleEvents = visibleEvents.filter(ev => reservedEventIds.has(String(ev.id)));
          }
        } else if (role !== 'Admin' && role !== 'SystemAdmin') {
          visibleEvents = [];
        }

        if (cancelled) return;
        setEvents(visibleEvents);

        const defaultEventId = visibleEvents[0]?.id || '';
        setSelectedEvent(prev => (prev && visibleEvents.some(e => e.id === prev) ? prev : defaultEventId));

        if (role === 'Vendor') {
          const vendorId = String(profile?.vendorId || '').trim();
          setSelectedVendor(vendorId);
          
          // Fetch vendor data to get the name
          if (vendorId) {
            try {
              const vendor = await getVendor(vendorId);
              if (vendor && !cancelled) {
                setVendorName(vendor.name || vendorId);
              }
            } catch (err) {
              console.error('Failed to fetch vendor name:', err);
              if (!cancelled) {
                setVendorName(vendorId);
              }
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load pre check-in setup.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedEvent || !isAdminLike) {
        setVendors([]);
        if (isAdminLike) setSelectedVendor('');
        return;
      }

      try {
        const reservationsCol = collection(db, 'boothReservations');
        const reservationsQuery = query(
          reservationsCol,
          where('eventId', '==', selectedEvent),
          where('status', '==', 'Reserved')
        );
        const reservationsSnap = await getDocs(reservationsQuery);
        if (cancelled) return;

        const vendorIds = Array.from(new Set(
          reservationsSnap.docs
            .map(d => String(d.data()?.vendorId || '').trim())
            .filter(Boolean)
        ));

        const vendorRows = await Promise.all(vendorIds.map(async (vendorId) => {
          try {
            const vendorSnap = await getDoc(doc(db, 'vendors', vendorId));
            if (!vendorSnap.exists()) {
              return { id: vendorId, name: vendorId, vendorNumber: '' };
            }
            const data = vendorSnap.data() || {};
            return {
              id: vendorId,
              name: String(data.name || data.vendorName || data.businessName || vendorId),
              vendorNumber: String(data.vendorNumber || data.vendorNo || '').trim(),
            };
          } catch {
            return { id: vendorId, name: vendorId, vendorNumber: '' };
          }
        }));

        const parseVendorNumber = (value) => {
          const text = String(value || '').trim();
          if (!text) return Number.POSITIVE_INFINITY;
          const match = text.match(/\d+/);
          if (!match) return Number.POSITIVE_INFINITY;
          const n = Number(match[0]);
          return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
        };

        vendorRows.sort((a, b) => {
          const an = parseVendorNumber(a.vendorNumber);
          const bn = parseVendorNumber(b.vendorNumber);
          if (an !== bn) return an - bn;
          const av = String(a.vendorNumber || '').toLowerCase();
          const bv = String(b.vendorNumber || '').toLowerCase();
          if (av !== bv) return av.localeCompare(bv);
          return String(a.name || '').localeCompare(String(b.name || ''));
        });

        if (cancelled) return;
        setVendors(vendorRows);
        setSelectedVendor(prev => (prev && vendorRows.some(v => v.id === prev) ? prev : (vendorRows[0]?.id || '')));
      } catch (e) {
        if (!cancelled) {
          setVendors([]);
          setSelectedVendor('');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [selectedEvent, isAdminLike]);

  useEffect(() => {
    const chosen = events.find(e => String(e.id) === String(selectedEvent)) || null;
    setEventData(chosen);
  }, [selectedEvent, events]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const vendorId = isVendor ? String(userProfile?.vendorId || '').trim() : String(selectedVendor || '').trim();
      if (!selectedEvent || !vendorId) {
        setPrecheckin(null);
        setCheckinRecord(null);
        setVendorBooths([]);
        setSelectedScheduleSequence('');
        setRequiredSelections({});
        setConsentName('');
        setConsentDate(getCurrentConsentDateDisplay());
        setConsentAccepted(false);
        setConsentViewed(false);
        setIsEditing(false);
        setPaperConsentForm(false);
        return;
      }

      try {
        setError('');
        const [existingPrecheckin, checkinSnap] = await Promise.all([
          getPrecheckin(selectedEvent, vendorId),
          getDoc(doc(db, CHECKINS_COLLECTION, `${selectedEvent}_${vendorId}`)),
        ]);

        const reservationsCol = collection(db, 'boothReservations');
        const purchasedQuery = query(
          reservationsCol,
          where('eventId', '==', selectedEvent),
          where('vendorId', '==', vendorId),
          where('status', '==', 'Reserved')
        );
        const purchasedSnap = await getDocs(purchasedQuery);

        if (cancelled) return;

        const checkinData = checkinSnap.exists() ? { id: checkinSnap.id, ...checkinSnap.data() } : null;
        const boothMap = new Map();
        purchasedSnap.forEach((d) => {
          const data = d.data() || {};
          const boothNumber = getReservationBoothNumber(data);
          if (!boothNumber) return;
          const boothKey = boothNumber.toLowerCase();
          if (!boothMap.has(boothKey)) {
            boothMap.set(boothKey, { boothKey, boothNumber });
          }
        });
        const parseBoothForSort = (value) => {
          const text = normalizeBoothNumber(value);
          const match = text.match(/\d+/);
          if (!match) return Number.POSITIVE_INFINITY;
          const n = Number(match[0]);
          return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
        };
        const sortedVendorBooths = Array.from(boothMap.values()).sort((a, b) => {
          const an = parseBoothForSort(a.boothNumber);
          const bn = parseBoothForSort(b.boothNumber);
          if (an !== bn) return an - bn;
          return String(a.boothNumber || '').localeCompare(String(b.boothNumber || ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });

        setCheckinRecord(checkinData);
        setVendorBooths(sortedVendorBooths);
        setHasPurchasedBooth(sortedVendorBooths.length > 0);
        setPrecheckin(existingPrecheckin);

        const chosenEvent = events.find(e => String(e.id) === String(selectedEvent));
        const chosenSchedule = Array.isArray(chosenEvent?.checkinSchedule) ? chosenEvent.checkinSchedule : [];
        const savedScheduleDate = String(
          existingPrecheckin?.selectedCheckinScheduleDate ||
          existingPrecheckin?.selectedCheckinSchedule?.date ||
          ''
        ).trim();
        const matchedSchedule = chosenSchedule.find(row => String(row?.date || '').trim() === savedScheduleDate);
        const savedScheduleSeq =
          matchedSchedule?.sequence ??
          existingPrecheckin?.selectedCheckinSchedule?.sequence ??
          existingPrecheckin?.selectedScheduleSequence ??
          '';
        setSelectedScheduleSequence(savedScheduleSeq !== '' ? String(savedScheduleSeq) : '');

        const savedRequired = {};
        const boothKeyByNumber = new Map(
          sortedVendorBooths.map((row) => [String(row.boothNumber || '').trim().toLowerCase(), row.boothKey])
        );
        const savedRequiredRows = Array.isArray(existingPrecheckin?.requiredItemSelections)
          ? existingPrecheckin.requiredItemSelections
          : (Array.isArray(existingPrecheckin?.requiredItemsSelections) ? existingPrecheckin.requiredItemsSelections : []);
        savedRequiredRows.forEach((row) => {
          const seq = Number(row?.sequence ?? 0);
          const name = String(row?.name || row?.itemName || '').trim();
          const itemKey = `${seq}::${name}`;
          const nameKey = String(row?.name || '').trim().toLowerCase();
          const persistedBoothNumber = String(row?.boothNumber || row?.booth || '').trim().toLowerCase();

          if (persistedBoothNumber) {
            const boothKey = boothKeyByNumber.get(persistedBoothNumber);
            if (boothKey && nameKey) {
              savedRequired[`${boothKey}::${itemKey}`] = true;
              savedRequired[`${boothKey}::${nameKey}`] = true;
            }
            return;
          }

          // Backward compatibility for older records that were saved without booth-level selection.
          sortedVendorBooths.forEach((booth) => {
            if (!nameKey) return;
            savedRequired[`${booth.boothKey}::${itemKey}`] = true;
            savedRequired[`${booth.boothKey}::${nameKey}`] = true;
          });
        });
        setRequiredSelections(savedRequired);

        setConsentName(String(existingPrecheckin?.consentFullName || existingPrecheckin?.consent?.fullName || existingPrecheckin?.consent?.printFullName || ''));
        setConsentDate(String(existingPrecheckin?.consentDate || existingPrecheckin?.consent?.signedDate || getCurrentConsentDateDisplay()));
        setConsentAccepted(
          existingPrecheckin?.consentSigned === true ||
          existingPrecheckin?.consent?.signed === true ||
          existingPrecheckin?.consent?.agreed === true
        );
        setConsentViewed(false);
        setIsEditing(false);
      } catch (e) {
        if (!cancelled) {
          setVendorBooths([]);
          setHasPurchasedBooth(false);
          setError(e?.message || 'Failed to load pre check-in record.');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [selectedEvent, selectedVendor, isVendor, userProfile?.vendorId, events]);

  const toggleRequired = (boothKey, item, index) => {
    const key = normalizeRequiredBoothItemKey(boothKey, item, index);
    const nameKey = String(item?.name || '').trim().toLowerCase();
    setRequiredSelections(prev => {
      const scopedNameKey = `${String(boothKey || '').trim()}::${nameKey}`;
      const nextValue = !(prev[scopedNameKey] === true || prev[key] === true);
      return { ...prev, [key]: nextValue, [scopedNameKey]: nextValue };
    });
  };

  const handleSave = async () => {
    const vendorId = isVendor ? String(userProfile?.vendorId || '').trim() : String(selectedVendor || '').trim();
    const showValidationError = (message) => {
      window.alert(message);
    };

    setError('');
    setSuccess('');

    if (!selectedEvent) {
      showValidationError('Please select an event.');
      return;
    }
    if (!vendorId) {
      showValidationError('Please select a vendor.');
      return;
    }
    if (!hasPurchasedBooth) {
      showValidationError('This vendor has not purchased a booth for the selected event. Pre check-in form is read-only.');
      return;
    }
    if (checkedIn) {
      showValidationError('This vendor is already checked in. Pre check-in data cannot be changed.');
      return;
    }
    if (showCheckinScheduleSection && isCheckinScheduleRequired && !selectedScheduleSequence) {
      showValidationError('Please select one check-in schedule option.');
      return;
    }
    if (!consentName.trim()) {
      showValidationError('Please enter the full name for consent form signature.');
      return;
    }
    if (!consentAccepted) {
      if (consentViewed) {
        showValidationError('Please check the consent acknowledgment box inside the consent form popup before submitting.');
      } else {
        showValidationError('Please open "View and Sign Consent Form" and acknowledge before submitting.');
      }
      return;
    }

    const consentDateDisplay = getCurrentConsentDateDisplay();
    setConsentDate(consentDateDisplay);

    const selectedSchedule = showCheckinScheduleSection
      ? sortedCheckinSchedule.find((r) => String(r?.sequence ?? '') === String(selectedScheduleSequence))
      : null;

    if (showCheckinScheduleSection) {
      if (selectedScheduleSequence && !selectedSchedule) {
        showValidationError('Selected schedule option is no longer available. Please choose again.');
        return;
      }
      if (isCheckinScheduleRequired && sortedCheckinSchedule.length === 0) {
        showValidationError('Check-in schedule is required for this event but no schedule options are configured.');
        return;
      }
      if (isCheckinScheduleRequired && !selectedSchedule) {
        showValidationError('Please select one check-in schedule option.');
        return;
      }
    }

    const requiredItemsSelections = showRequiredItemsSection
      ? vendorBooths.flatMap((booth) => sortedRequiredItems
        .map((item, index) => {
          const key = normalizeRequiredBoothItemKey(booth.boothKey, item, index);
          const nameKey = `${booth.boothKey}::${String(item?.name || '').trim().toLowerCase()}`;
          return {
            key,
            nameKey,
            sequence: Number(item?.sequence ?? (index + 1)),
            name: String(item?.name || ''),
            boothNumber: String(booth.boothNumber || ''),
          };
        })
        .filter(item => requiredSelections[item.nameKey] === true || requiredSelections[item.key] === true)
        .map(item => ({ sequence: item.sequence, name: item.name, boothNumber: item.boothNumber })))
      : [];

    if (showRequiredItemsSection && isRequiredItemsRequired) {
      if (sortedRequiredItems.length === 0) {
        showValidationError('Required items are required for this event but no required-item options are configured.');
        return;
      }
      if (requiredItemsSelections.length === 0) {
        showValidationError('Please select at least one required item.');
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        selectedCheckinScheduleDate: showCheckinScheduleSection ? String(selectedSchedule?.date || '') : '',
        requiredItemSelections: requiredItemsSelections,
        consentSigned: true,
        consentFullName: consentName.trim(),
        consentDate: consentDateDisplay,
        paperConsentForm: paperConsentForm,
      };

      const saved = await upsertPrecheckin({
        eventId: selectedEvent,
        vendorId,
        data: payload,
        userUid: currentUser?.uid || null,
      });

      setPrecheckin(saved);
      setIsEditing(false);
      setSuccess('');
      window.alert('Pre check-in steps saved successfully.');
    } catch (e) {
      setError(e?.message || 'Failed to save pre check-in steps.');
    } finally {
      setSaving(false);
    }
  };

  const renderConsentModal = () => {
    if (!showConsentModal) return null;
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 8,
            maxWidth: 900,
            width: '100%',
            maxHeight: '82vh',
            overflow: 'auto',
            padding: 16,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Vendor Consent Form</h3>
          </div>
          <div style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', overflowX: 'hidden' }}>
            {String(eventData?.consentMessage || '').trim()
              ? <div className="consent-modal-html" dangerouslySetInnerHTML={{ __html: eventData.consentMessage }} />
              : <div className="profile-hint">No consent text available for this event.</div>}
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <label className="profile-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #d4deea', borderRadius: 8, background: '#f7fbff' }}>
              <input
                type="checkbox"
                className="consent-sign-checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                disabled={readOnly}
              />
              By checking this box, I sign this consent form.
            </label>
            <button type="button" className="cancel-btn" onClick={() => setShowConsentModal(false)}>OK</button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-hint">Loading pre check-in steps...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => handleExitPage(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => handleExitPage('/')}>Close</button>
      </div>

      <h2 className="profile-title">Pre Checkin & Consent Form</h2>

      {error && <div className="profile-error" style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="profile-hint" style={{ marginBottom: 12, color: '#2e7d32' }}>{success}</div>}

      <div className="profile-grid" style={{ marginBottom: 12 }}>
        <div>
          <label className="profile-label">Event</label>
          <select
            className="profile-input"
            value={selectedEvent}
            onChange={(e) => {
              setSelectedEvent(e.target.value);
              setSuccess('');
            }}
          >
            <option value="">-- Select Event --</option>
            {events.map(ev => (
              <option key={ev.id} value={ev.id}>{ev.name || ev.id}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="profile-label">Vendor</label>
          {isVendor ? (
            <input className="profile-input" value={vendorName || 'No linked vendor'} disabled />
          ) : (
            <select
              className="profile-input"
              value={selectedVendor}
              onChange={(e) => {
                setSelectedVendor(e.target.value);
                setSuccess('');
              }}
              disabled={!selectedEvent}
            >
              <option value="">-- Select Vendor --</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.vendorNumber ? `${v.vendorNumber} - ${v.name}` : v.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {checkedIn && (
        <div className="profile-hint" style={{ marginBottom: 12, color: '#b71c1c' }}>
          This vendor is already checked in. The pre check-in form is locked.
        </div>
      )}

      {!checkedIn && !hasPurchasedBooth && !!selectedEvent && !!(isVendor ? userProfile?.vendorId : selectedVendor) && (
        <div className="profile-hint" style={{ marginBottom: 12, color: '#b71c1c' }}>
          This vendor has not purchased any booths for the selected event. The pre check-in form is read-only.
        </div>
      )}

      {signed && !checkedIn && hasPurchasedBooth && !isEditing && (
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="apply-btn"
            onClick={() => {
              // Clear the consentSigned flag so user must re-accept
              setPrecheckin(prev => (prev ? { ...prev, consentSigned: false, consent: { ...prev?.consent, signed: false } } : prev));
              setIsEditing(true);
              setConsentAccepted(false);
              setConsentViewed(false);
              setSuccess('');
            }}
          >
            Edit
          </button>
        </div>
      )}

      {!!selectedEvent && !!(isVendor ? userProfile?.vendorId : selectedVendor) && (
        <>
          {showCheckinScheduleSection && (
            <div style={{ marginBottom: 18 }}>
              <h3 className="section-title" style={sectionTitleStyle}>Checkin Schedule</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sortedCheckinSchedule.map((row, index) => (
                  <div
                    key={`sched-${row.sequence ?? index}`}
                    style={{
                      border: String(selectedScheduleSequence) === String(row.sequence ?? '') ? '1px solid #1976d2' : '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: 8,
                      background: String(selectedScheduleSequence) === String(row.sequence ?? '') ? '#f6faff' : '#fff',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '86px 70px minmax(90px, 1fr) minmax(90px, 1fr)', alignItems: 'center', columnGap: 8, rowGap: 4 }}>
                      <div style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className={String(selectedScheduleSequence) === String(row.sequence ?? '') ? 'apply-btn' : 'cancel-btn'}
                          onClick={() => setSelectedScheduleSequence(String(row.sequence ?? ''))}
                          disabled={readOnly}
                          style={{
                            width: '100%',
                            minWidth: 0,
                            padding: '6px 4px',
                            fontSize: 11,
                            whiteSpace: 'nowrap',
                            cursor: readOnly ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {String(selectedScheduleSequence) === String(row.sequence ?? '') ? 'Selected' : 'Select'}
                        </button>
                      </div>
                      <div style={{ textAlign: 'center', fontWeight: 600 }}>{row.sequence ?? (index + 1)}</div>
                      <div><strong>Date:</strong> {row.date || '-'}</div>
                      <div><strong>Time:</strong> {row.time || '-'}</div>
                    </div>
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #e5e7eb', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', color: '#4a5568', fontSize: 13 }}>
                      <strong>Message:</strong> {row.message || '-'}
                    </div>
                  </div>
                ))}
                {sortedCheckinSchedule.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#666', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    No check-in schedule is configured for this event.
                  </div>
                )}
              </div>
            </div>
          )}

          {showRequiredItemsSection && (
            <div style={{ marginBottom: 18 }}>
              <h3 className="section-title" style={sectionTitleStyle}>Required Items</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {vendorBooths.map((booth) => (
                  <div key={`booth-required-${booth.boothKey}`} style={{ border: '1px solid #d8e3f0', borderRadius: 8, padding: '7px 8px', background: '#f9fcff' }}>
                    <div style={{ marginBottom: 6, fontWeight: 700, color: '#16324f', fontSize: 13, lineHeight: 1.2 }}>
                      Booth Number: {booth.boothNumber || '-'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {sortedRequiredItems.map((item, index) => {
                        const itemKey = normalizeRequiredBoothItemKey(booth.boothKey, item, index);
                        const scopedNameKey = `${booth.boothKey}::${String(item?.name || '').trim().toLowerCase()}`;
                        return (
                          <div key={`item-${booth.boothKey}-${itemKey}`} style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 7px', background: '#fff' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '32px 56px minmax(130px, 1fr)', alignItems: 'center', columnGap: 6, rowGap: 2 }}>
                              <div style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  className="consent-sign-checkbox"
                                  checked={requiredSelections[scopedNameKey] === true || requiredSelections[itemKey] === true}
                                  onChange={() => toggleRequired(booth.boothKey, item, index)}
                                  disabled={readOnly}
                                />
                              </div>
                              <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 12 }}>{item.sequence ?? (index + 1)}</div>
                              <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', fontSize: 13, lineHeight: 1.25 }}><strong>Item:</strong> {item.name || '-'}</div>
                            </div>
                            <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px dashed #e5e7eb', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', color: '#4a5568', fontSize: 12, lineHeight: 1.25 }}>
                              <strong>Message:</strong> {item.message || '-'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {(vendorBooths.length === 0 || sortedRequiredItems.length === 0) && (
                  <div style={{ textAlign: 'center', color: '#666', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    {sortedRequiredItems.length === 0
                      ? 'No required items are configured for this event.'
                      : 'No reserved booths were found for this vendor.'}
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <h3 className="section-title" style={sectionTitleStyle}>Vendor Consent Form</h3>
            <div style={{ marginBottom: 10 }}>
              <button
                type="button"
                className="new-layout-btn"
                onClick={() => {
                  setConsentViewed(true);
                  setShowConsentModal(true);
                }}
              >
                View and Sign Consent Form
              </button>
            </div>
            <div className="profile-grid">
              <div>
                <label className="profile-label">Print Full Name</label>
                <input
                  className="profile-input"
                  value={consentName}
                  onChange={(e) => setConsentName(e.target.value)}
                  disabled={readOnly}
                  placeholder="Type full legal name"
                />
                {showSignedStatus && (
                  <div className="profile-hint" style={{ marginTop: 6, color: '#2e7d32', fontWeight: 600 }}>
                    Form has been Signed
                  </div>
                )}
              </div>
              <div>
                <label className="profile-label">Consent Date and Time</label>
                <input
                  className="profile-input"
                  value={consentDate}
                  readOnly
                  disabled
                />
              </div>
            </div>
            {!consentViewed && !readOnly && (
              <div className="profile-hint" style={{ marginTop: 6 }}>
                Please open the consent form and sign it before submitting.
              </div>
            )}
          </div>

          <div className="profile-actions">
            <button
              type="button"
              className="apply-btn"
              onClick={handleSave}
              disabled={saving || readOnly}
              title={readOnly ? 'Form is read-only and cannot be submitted.' : 'Submit pre check-in form'}
            >
              {saving ? 'Saving...' : 'Submit'}
            </button>
            {!isVendor && (
              <label style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={paperConsentForm}
                  onChange={(e) => setPaperConsentForm(e.target.checked)}
                />
                Paper Consent Form
              </label>
            )}
            {isEditing && (
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  handleExitPage(-1);
                }}
                style={{ marginLeft: 8 }}
              >
                Cancel
              </button>
            )}
          </div>
        </>
      )}

      {renderConsentModal()}
    </div>
  );
};

export default PreCheckinSteps;
